import { useRef, useState, useEffect, useCallback, useContext } from 'react'
import classNames from 'classnames'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles, useTheme } from 'react-jss'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import { Context as LoadingContext } from '@/context/loading'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import MainHero from '@/components/MainHero'
import BtnLink from '@/components/BtnLink'
import RollTextOnScroll from '@/components/RollTextOnScroll'
import ImageZoom from '@/components/ImageZoom'
import ParallaxText from '@/components/ParallaxText'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import { externalPreload } from '@/utils/dom'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const HomePage = ({
  stub,
}) => {
  const classes = useStyles()
  const { getImageUrl, setHomepage } = useContext(GeneralContext)
  const { setReady, ready } = useContext(LoadingContext)
  const theme = useTheme()

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub] || {},
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])

  const $root = useRef()
  const preload = useRef(new CreateJs.LoadQueue())
  const [load, setLoad] = useState(false)

  /*------------------------------
  Hooks to handle media loading & updating the scrollbar
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Preload Media
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.hph_img, 'full', stub, 'pages')
    const featuredMedia = getImageUrl(page.hph_roll_img, 'large', stub, 'pages')
    const heroMedia2 = getImageUrl(page.hph_banner_img, 'full', stub, 'pages')
    preload.current.loadFile(heroMedia, featuredMedia, heroMedia2)
  }

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      preloadMedia()
    }
  }, [page])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
    setHomepage(true)
    return () => setHomepage(false)
  }, [])

  /*------------------------------
  Trigger resize to update scroll
  ------------------------------*/
  useEffect(() => {
    if (load && !theme.detect.isIE11) window.dispatchEvent(new Event('resize'))
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Cover Home
  ------------------------------*/
  const renderCoverHome = () => {
    return (page.hph_title1 && page.hph_img) && (
      <MainHero
        title={[page.hph_title1, page.hph_title2]}
        link={page.hph_link}
        img={getImageUrl(page.hph_img, 'full', stub, 'pages')}
        video={page.hph_video}
        ready={ready}
        layout="bottom"
        scrollIcon
        parallax
        delay={1}
        background="light"
      />
    )
  }

  /*------------------------------
  Render Home Roll Text
  ------------------------------*/
  const renderHomeRollText = () => {
    return (page.hph_roll_title && page.hph_roll_img) && (
      <div
        className={classNames({
          [classes.wrapper]: true,
          [classes.wrapperSpacingVertical]: true,
          [classes.wrapperColumn]: true,
          [classes.wrapperRoll]: true,
        })}
      >
        <div className={classes.rollContent}>
          <div className={classes.rollImg}>
            <ImageZoom
              img={getImageUrl(page.hph_roll_img, 'large', stub, 'pages')}
              alt={page.hph_roll_title}
              width={300}
              height={300}
              scaleImgOnScroll
            />
          </div>
          <div className={classes.rollTitle}>
            <RollTextOnScroll
              tag="h2"
              text={page.hph_roll_title}
              reverse
            />
          </div>
        </div>
        <div className={classes.rollCta}>
          <BtnLink
            label={page.hph_roll_label !== '' ? page.hph_roll_label : strings.discover}
            link={page.hph_roll_link}
            theme="quicksand"
          />
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Banner Home
  ------------------------------*/
  const renderBannerHome = () => {
    return (page.hp_banner_title && page.hph_banner_img) && (
      <MainHero
        title={page.hp_banner_title.split('<br>')}
        link={page.hp_banner_link}
        ctaLabel={page.hp_banner_label || strings.discover}
        img={getImageUrl(page.hph_banner_img, 'full', stub, 'pages')}
        ready={ready}
        bigFont
        animateOnScroll
        delay={0.5}
        color="cream"
      />
    )
  }

  /*------------------------------
  Render Parallax Text
  ------------------------------*/
  const renderParallaxText = () => {
    const img1 = (page.parallax_img_1 !== '') ? getImageUrl(page.parallax_img_1, 'large', stub) : null
    const img2 = (page.parallax_img_2 !== '') ? getImageUrl(page.parallax_img_2, 'large', stub) : null
    const img3 = (page.parallax_img_3 !== '') ? getImageUrl(page.parallax_img_3, 'large', stub) : null
    const img4 = (page.parallax_img_4 !== '') ? getImageUrl(page.parallax_img_4, 'large', stub) : null
    const img5 = (page.parallax_img_5 !== '') ? getImageUrl(page.parallax_img_5, 'large', stub) : null

    return page.parallax_text && (
      <ParallaxText
        className={classes.parallaxText}
        data={{
          text: page.parallax_text,
          img1,
          img2,
          img3,
          img4,
          img5,
          label: page.parallax_label,
          link: page.parallax_link,
        }}
      />
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCoverHome()}
        {renderHomeRollText()}
        {renderBannerHome()}
        {renderParallaxText()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageHome ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default HomePage
